// src/features/dashboard/components/EmployeeProductivity/MonthlyProductivityTrendChart.tsx

import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  LabelList,
} from 'recharts';

import type {
  MonthlyProductivityTrendItem,
} from '../../types/Employee/employeeProductivity.types';

interface Props {
  data: MonthlyProductivityTrendItem[];
  target: number;
}

const COLORS = {
  line: '#22C55E',
  area: '#22C55E',
  target: '#EF4444',
  grid: '#EAECF0',
};

/* =========================================================
   TOOLTIP
========================================================= */

const CustomTooltip = ({
  active,
  payload,
  label,
}: any) => {
  if (
    !active ||
    !payload ||
    payload.length === 0
  ) {
    return null;
  }

  const productivity =
    payload.find(
      (item: any) =>
        item.dataKey === 'productivity',
    );

  return (
    <div
      className="
        min-w-[145px]
        rounded-[8px]
        border
        border-[#D0D5DD]
        bg-white
        px-[12px]
        py-[9px]
        shadow-lg
      "
    >
      <p
        className="
          mb-[8px]
          text-[12px]
          font-semibold
          text-[#101828]
        "
      >
        Day {label}
      </p>

      <div
        className="
          flex
          items-center
          justify-between
          gap-[12px]
          whitespace-nowrap
          text-[11px]
        "
      >
        <span
          className="
            flex
            items-center
            gap-[6px]
            text-[#22C55E]
          "
        >
          <span
            className="
              h-[7px]
              w-[7px]
              rounded-full
              bg-[#22C55E]
            "
          />

          Productivity
        </span>

        <span
          className="
            font-semibold
            text-[#101828]
          "
        >
          {productivity?.value ?? 0}%
        </span>
      </div>
    </div>
  );
};

/* =========================================================
   PRODUCTIVITY LABEL
========================================================= */

const ValueLabel = ({
  x,
  y,
  value,
}: any) => {
  if (
    x == null ||
    y == null ||
    value == null
  ) {
    return null;
  }

  return (
    <g>
      <rect
        x={x - 14}
        y={y - 20}
        width={28}
        height={16}
        rx={3}
        fill={COLORS.line}
      />

      <text
        x={x}
        y={y - 9}
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={10}
        fill="#FFFFFF"
        fontWeight={600}
      >
        {value}
      </text>
    </g>
  );
};

/* =========================================================
   TARGET LABEL
========================================================= */

const TargetLabel = ({
  viewBox,
}: any) => {
  if (!viewBox) {
    return null;
  }

  const {
    x = 0,
    y = 0,
    width = 0,
  } = viewBox;

  return (
    <g>
      <rect
        x={x + width - 48}
        y={y - 18}
        width={46}
        height={18}
        rx={3}
        fill="#FFFFFF"
        stroke="#F97066"
        strokeWidth={1}
      />

      <text
        x={x + width - 25}
        y={y - 7}
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={10}
        fontWeight={600}
        fill="#F97066"
      >
        Target
      </text>
    </g>
  );
};

/* =========================================================
   DYNAMIC Y-AXIS
========================================================= */

const getYAxisConfig = (
  data: MonthlyProductivityTrendItem[],
  target: number,
) => {
  const values = data
    .map((item) => Number(item.productivity))
    .filter(
      (value) =>
        Number.isFinite(value),
    );

  const safeTarget = Number.isFinite(target)
    ? Number(target)
    : 80;

  /*
   * No data fallback.
   */
  if (values.length === 0) {
    return {
      min: Math.max(
        0,
        Math.floor(
          (safeTarget - 20) / 10,
        ) * 10,
      ),
      max: Math.min(
        100,
        Math.ceil(
          (safeTarget + 20) / 10,
        ) * 10,
      ),
      ticks: [60, 70, 80, 90, 100],
    };
  }

  const minValue = Math.min(
    ...values,
    safeTarget,
  );

  const maxValue = Math.max(
    ...values,
    safeTarget,
  );

  /*
   * Add some breathing room above/below
   * the actual values.
   */
  const range = maxValue - minValue;

  /*
   * Minimum padding prevents the chart
   * from looking too compressed when values
   * are very close together.
   */
  const padding =
    range <= 10
      ? 10
      : Math.max(
          5,
          Math.ceil(range * 0.15),
        );

  let min = Math.floor(
    (minValue - padding) / 10,
  ) * 10;

  let max = Math.ceil(
    (maxValue + padding) / 10,
  ) * 10;

  /*
   * Keep productivity within a sensible
   * percentage range.
   */
  min = Math.max(0, min);
  max = Math.min(100, max);

  /*
   * If min/max become equal, expand them.
   */
  if (min === max) {
    min = Math.max(0, min - 10);
    max = Math.min(100, max + 10);
  }

  /*
   * Generate dynamic ticks.
   *
   * Example:
   * 40, 50, 60, 70, 80
   *
   * or:
   * 50, 60, 70, 80, 90
   */
  const ticks: number[] = [];

  for (
    let value = min;
    value <= max;
    value += 10
  ) {
    ticks.push(value);
  }

  /*
   * Safety fallback.
   */
  if (ticks.length < 2) {
    ticks.push(
      Math.min(100, min + 10),
    );
  }

  return {
    min,
    max,
    ticks,
  };
};

/* =========================================================
   MAIN COMPONENT
========================================================= */

const MonthlyProductivityTrendChart = ({
  data,
  target,
}: Props) => {
  const [zoom, setZoom] =
    useState<number>(1);

  const chartRef =
    useRef<HTMLDivElement | null>(null);

  /* =======================================================
     VISIBLE DATA
  ======================================================= */

  const visibleData = useMemo(() => {
    if (
      !Array.isArray(data) ||
      data.length === 0
    ) {
      return [];
    }

    /*
     * 1x = all data
     */
    if (zoom === 1) {
      return data;
    }

    /*
     * Zoom in by reducing visible points.
     */
    const visibleCount =
      Math.max(
        3,
        Math.round(
          data.length / zoom,
        ),
      );

    return data.slice(
      Math.max(
        0,
        data.length - visibleCount,
      ),
    );
  }, [data, zoom]);

  /* =======================================================
     DYNAMIC Y-AXIS
  ======================================================= */

  const yAxisConfig = useMemo(
    () =>
      getYAxisConfig(
        visibleData,
        Number(target),
      ),
    [visibleData, target],
  );

  /* =======================================================
     WHEEL ZOOM
  ======================================================= */

  useEffect(() => {
    const element =
      chartRef.current;

    if (!element) {
      return;
    }

    const handleWheel = (
      event: WheelEvent,
    ) => {
      /*
       * Keep existing zoom behavior.
       */
      event.preventDefault();
      event.stopPropagation();

      setZoom((currentZoom) => {
        let nextZoom =
          currentZoom;

        /*
         * Scroll UP = Zoom IN
         */
        if (event.deltaY < 0) {
          nextZoom =
            currentZoom + 0.25;
        }

        /*
         * Scroll DOWN = Zoom OUT
         */
        if (event.deltaY > 0) {
          nextZoom =
            currentZoom - 0.25;
        }

        /*
         * Keep between 1x and 3x.
         */
        nextZoom = Math.max(
          1,
          Math.min(
            3,
            nextZoom,
          ),
        );

        return Number(
          nextZoom.toFixed(2),
        );
      });
    };

    element.addEventListener(
      'wheel',
      handleWheel,
      {
        passive: false,
      },
    );

    return () => {
      element.removeEventListener(
        'wheel',
        handleWheel,
      );
    };
  }, []);

  /* =======================================================
     EMPTY STATE
  ======================================================= */

  if (visibleData.length === 0) {
    return (
      <div
        className="
          h-full
          min-h-[300px]
          w-full
          min-w-0
          max-w-full
          rounded-[16px]
          border
          border-[#E4E7EC]
          bg-white
          p-[18px]
          shadow-sm
        "
      >
        <div
          className="
            flex
            flex-wrap
            items-center
            gap-[8px]
          "
        >
          <h3
            className="
              text-[14px]
              font-semibold
              tracking-[-0.2px]
              text-[#0F2B5B]
            "
          >
            Monthly Productivity Trend
          </h3>

          <div
            className="
              rounded-full
              bg-[#EEF4FF]
              px-[10px]
              py-[5px]
              text-[10px]
              font-medium
              text-[#2563EB]
            "
          >
            May 2026 · daily % · 30-day view
          </div>
        </div>

        <div
          className="
            flex
            h-[230px]
            items-center
            justify-center
            text-[12px]
            text-[#667085]
          "
        >
          No data available
        </div>
      </div>
    );
  }

  return (
    <div
      className="
        box-border
        h-full
        min-h-[300px]
        w-full
        min-w-0
        max-w-full
        overflow-hidden
        rounded-[16px]
        border
        border-[#E4E7EC]
        bg-white
        p-[14px]
        shadow-sm

        sm:p-[16px]

        lg:p-[18px]
      "
    >
      {/* =================================================
          HEADER
      ================================================= */}

      <div
        className="
          mb-[14px]
          flex
          w-full
          min-w-0
          max-w-full
          flex-wrap
          items-center
          gap-[8px]
        "
      >
        <h3
          className="
            min-w-0
            shrink
            text-[14px]
            font-semibold
            tracking-[-0.2px]
            text-[#0F2B5B]
          "
        >
          Monthly Productivity Trend
        </h3>

        <div
          className="
            max-w-full
            shrink-0
            overflow-hidden
            text-ellipsis
            whitespace-nowrap
            rounded-full
            bg-[#EEF4FF]
            px-[10px]
            py-[5px]
            text-[10px]
            font-medium
            text-[#2563EB]
          "
        >
          May 2026 · daily % · 30-day view
        </div>
      </div>

      {/* =================================================
          CHART
      ================================================= */}

      <div
        ref={chartRef}
        className="
          relative
          h-[210px]
          w-full
          min-w-0
          max-w-full
          overflow-hidden
          select-none

          sm:h-[220px]

          lg:h-[230px]
        "
        style={{
          overscrollBehavior:
            'contain',
          touchAction: 'pan-x',
        }}
      >
        <ResponsiveContainer
          width="100%"
          height="100%"
        >
          <ComposedChart
            data={visibleData}
            margin={{
              top: 28,
              right: 42,
              left: 30,
              bottom: 10,
            }}
          >
            {/* =================================================
                GRID
            ================================================= */}

            <CartesianGrid
              vertical={false}
              horizontal
              stroke={COLORS.grid}
            />

            {/* =================================================
                X AXIS
            ================================================= */}

            <XAxis
              dataKey="day"
              axisLine={false}
              tickLine={false}
              tickMargin={8}
              interval={2}
              tick={{
                fontSize: 9,
                fill: '#667085',
              }}
            />

            {/* =================================================
                DYNAMIC Y AXIS
            ================================================= */}

            <YAxis
              domain={[
                yAxisConfig.min,
                yAxisConfig.max,
              ]}
              ticks={
                yAxisConfig.ticks
              }
              allowDataOverflow={false}
              axisLine={false}
              tickLine={false}
              width={38}
              tickFormatter={(value) =>
                `${value}%`
              }
              tick={{
                fontSize: 9,
                fill: '#667085',
              }}
            />

            {/* =================================================
                TOOLTIP
            ================================================= */}

            <Tooltip
              content={
                <CustomTooltip />
              }
              cursor={{
                stroke: '#98A2B3',
                strokeDasharray:
                  '3 3',
                strokeWidth: 1,
              }}
              wrapperStyle={{
                zIndex: 1000,
              }}
            />

            {/* =================================================
                TARGET
            ================================================= */}

            <ReferenceLine
              y={Number(target)}
              stroke="#F97066"
              strokeDasharray="4 3"
              strokeWidth={1}
              label={
                <TargetLabel />
              }
            />

            {/* =================================================
                AREA
            ================================================= */}

            <Area
              type="monotone"
              dataKey="productivity"
              stroke="none"
              fill={COLORS.area}
              fillOpacity={0.06}
              animationDuration={300}
            />

            {/* =================================================
                LINE
            ================================================= */}

            <Line
              type="monotone"
              dataKey="productivity"
              stroke={COLORS.line}
              strokeWidth={2}
              connectNulls
              animationDuration={300}
              dot={{
                r: 5,
                fill: COLORS.line,
                stroke: '#FFFFFF',
                strokeWidth: 2,
              }}
              activeDot={{
                r: 6,
                fill: COLORS.line,
                stroke: '#FFFFFF',
                strokeWidth: 2,
              }}
            >
              <LabelList
                dataKey="productivity"
                content={
                  <ValueLabel />
                }
              />
            </Line>
          </ComposedChart>
        </ResponsiveContainer>

        {/* =================================================
            ZOOM INDICATOR
        ================================================= */}

        <div
          className="
            pointer-events-none
            absolute
            right-[6px]
            top-[6px]
            rounded-full
            bg-[#F0FDF4]
            px-[8px]
            py-[4px]
            text-[9px]
            font-medium
            text-[#16A34A]
          "
        >
          {zoom.toFixed(2)}x
        </div>
      </div>
    </div>
  );
};

export default MonthlyProductivityTrendChart;